/**
 * Streaming guards for `![alt](asset:<token>)` images in an answer.
 *
 * While an answer is written out, a half-written image ref renders as raw
 * markdown text (`![Lime can](asset:4506…`) until its closing parenthesis
 * lands. The answer body holds that tail back instead, the way a rich fence
 * renders as a skeleton until it parses, and the image tile beside the answer
 * stays until the ref on screen is complete.
 */

const ASSET_SCHEME = 'asset:'

/** A complete image ref whose target is an `asset:` token. */
const COMPLETE_ASSET_IMAGE = /!\[[^\]\n]*\]\(asset:([A-Za-z0-9_-]{1,64})\)/g

/** `![alt` still open: the target is not known yet, so it may become an asset image. */
const OPEN_ALT = /^!\[[^\]\n]*\]?$/

/** `![alt](target` without its closing parenthesis. */
const OPEN_TARGET = /^!\[[^\]\n]*\]\(([^)\s]*)$/

function couldBecomeAssetTarget(target: string): boolean {
  return target.startsWith(ASSET_SCHEME) || ASSET_SCHEME.startsWith(target)
}

/**
 * Drops a trailing image ref that is still being written and may resolve to
 * an `asset:` token. A complete ref, prose, and an unfinished image pointing
 * anywhere other than `asset:` pass through untouched.
 */
export function holdBackPartialAssetImage(text: string): string {
  const start = text.lastIndexOf('![')
  if (start < 0) {
    return text
  }
  const tail = text.slice(start)
  if (OPEN_ALT.test(tail)) {
    return text.slice(0, start)
  }
  const openTarget = OPEN_TARGET.exec(tail)
  if (openTarget && couldBecomeAssetTarget(openTarget[1])) {
    return text.slice(0, start)
  }
  return text
}

/** Tokens of every complete `![alt](asset:<token>)` image, in order, deduped. */
export function placedAssetImageTokens(text: string): Array<string> {
  const tokens: Array<string> = []
  for (const match of text.matchAll(COMPLETE_ASSET_IMAGE)) {
    const token = match[1]
    if (!tokens.includes(token)) {
      tokens.push(token)
    }
  }
  return tokens
}
